import CircularLoader from 'components/CircularLoader';
import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import {
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
} from 'components/ui/combobox';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import get from 'lodash/get';
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Controller, FieldValues, SetFieldValue, useWatch } from 'react-hook-form';
import { groupConsecutiveOptions, OptionGroup } from 'utils/autocompleteGrouping';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import ReadonlyField from '../rhf-other/ReadonlyField';
import SelectedValueOverlay from './SelectedValueOverlay';
import { CommonInputProps, DeepKeyByValue, KeyByValue } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useAdornmentGetters } from './useAdornmentGetters';
import { useOwnPlaceholder } from './newDesignInput';
import { useMemoizedRules } from './useMemoizedRules';
import { PaginatedListProps, usePaginatedPopup } from './usePaginatedList';

type GetOptionIdentifier<TOption extends FieldValues> =
    | ((option: TOption) => string | number)
    | KeyByValue<TOption, number | string>;
type GetOptionLabel<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string | null>;

type GetOptionDescription<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string>;

type GetOptionEndAdornment<TOption extends FieldValues> = ((option: TOption) => ReactNode) | keyof TOption;

type GetOptionStartAdornment<TOption extends FieldValues> = ((option: TOption) => ReactNode) | keyof TOption;

type GetOptionAction<TOption extends FieldValues> = (option: TOption) => ReactNode;

type GetGroupStartAdornment<TOption extends FieldValues> = (group: string, options: TOption[]) => ReactNode;

type CustomOptionRenderer<TOption extends FieldValues> = (
    option: TOption,
    /**
     * Always empty. The row's own props — role, click, highlight — belong to the combobox item
     * this element is rendered inside, so there is nothing left to hand a renderer. The parameter
     * stays in the signature because every renderer already takes it positionally.
     */
    props: React.HTMLAttributes<HTMLLIElement>,
    getValue: (option: TOption) => string | number | null,
    /** The option's work email when another loaded option shares its name, else `null`. */
    sameNameEmail: string | null
) => { element: ReactNode; isDisabled: boolean };

export interface DynamicSelectInputProps<
    TFormData extends FieldValues,
    TOption extends FieldValues,
> extends CommonInputProps<TFormData> {
    placeholderOnFocus?: string;
    clearable?: boolean;
    onChange?: (value: TOption | null) => void;
    onSearchChange?: (eventType: string, searchValue: string) => void;
    getOptionLabel: GetOptionLabel<TOption>;
    setValue: SetFieldValue<TFormData>;
    getOptionIdentifier: GetOptionIdentifier<TOption>;
    groupSortOrder?: string[]; // legacy, does nothing but allows to preserve already set sort orders that could potentially be made functional again
    /**
     * Ensure that the value specified in `groupByField` is also included in the `fetch` sort options (`paginated.sort`)
     * with the same value to avoid duplicate grouping issues.
     */
    groupByField?: ((option: TOption) => string | number) | DeepKeyByValue<TOption, number | string>;
    /**
     * Function to group options by static groups. Takes precedence over groupByField.
     * Return null to disable grouping for specific options.
     */
    groupByStaticFields?: (option: TOption) => string | null;
    defaultGroup?: string;
    noOptionsContent?: React.ReactNode;
    readonly?: boolean | { linkTo: string };
    noReadOnlyDivider?: boolean;
    getOptionDescription?: GetOptionDescription<TOption>;
    getOptionEndAdornment?: GetOptionEndAdornment<TOption>;
    /** Rendered after `getOptionEndAdornment` on the selected value in the input only, never on the list rows. */
    getSelectedValueEndAdornment?: (option: TOption) => ReactNode;
    /** Rendered before the label on the selected value in the input only, never on the list rows. */
    getSelectedValueStartAdornment?: (option: TOption) => ReactNode;
    getOptionStartAdornment?: GetOptionStartAdornment<TOption>;
    getGroupStartAdornment?: GetGroupStartAdornment<TOption>;
    getOptionAction?: GetOptionAction<TOption>;
    customOptionRenderer?: CustomOptionRenderer<TOption>;
    paginated: PaginatedListProps<TOption>;
    /** When true, shows a spinner adornment at the end of the input — for signaling that data
     * tied to the selected value is being fetched externally. Independent from internal options loading. */
    loading?: boolean;
    /** Opens the option list as the field mounts — for a field swapped in by the click meant to open it. */
    openOnMount?: boolean;
    /** Classes for the option list's positioner — to raise it over a surface stacked above `--z-dropdown`. */
    positionerClassName?: string;
}

const DynamicSelectInput = <TFormData extends FieldValues, TOption extends FieldValues>({
    name,
    label,
    gap,
    shouldTranslateLabel = true,
    tooltip,
    required,
    rightLabelComponent,
    bottomRightLabelComponent,
    disabled,
    placeholder,
    shouldTranslatePlaceholder = true,
    placeholderOnFocus,
    wrapperClassName,
    control,
    clearable,
    onChange,
    onSearchChange,
    getOptionLabel,
    getOptionIdentifier,
    groupByField,
    groupByStaticFields,
    defaultGroup,
    validate,
    noOptionsContent,
    readonly = false,
    noReadOnlyDivider,
    getOptionDescription,
    getOptionEndAdornment,
    getSelectedValueEndAdornment,
    getSelectedValueStartAdornment,
    getOptionStartAdornment,
    getGroupStartAdornment,
    getOptionAction,
    customOptionRenderer,
    paginated,
    loading: externalLoading,
    openOnMount,
    positionerClassName,
}: DynamicSelectInputProps<TFormData, TOption>) => {
    const initialPlaceholder = useOwnPlaceholder(placeholder, label, shouldTranslatePlaceholder) ?? t('select');
    const [controlledPlaceholder, setControlledPlaceholder] = useState<string>(initialPlaceholder);
    // Tracks the live text in the combobox input so we can tell whether the user has started typing
    // a search query (overlay hides) or is still sitting on the selected value (overlay stays).
    const [inputValue, setInputValue] = useState<string>('');
    const {
        dataToDisplay,
        handleLoadMore,
        handleSearchChange,
        hasNextPage,
        isLoading,
        isError,
        isOpen: isDropdownOpen,
        isOpenPending,
        actionsRef,
        setOpen,
        dropPendingOpen,
        onInputKeyDown,
    } = usePaginatedPopup<TOption>(paginated, undefined, openOnMount);
    const rules = useMemoizedRules({ required, validate, requiredMessageId: 'validation.choose-option' });
    const fallbackTranslation = defaultGroup ?? t('field.other');
    const { getStartAdornment, getEndAdornment } = useAdornmentGetters(getOptionStartAdornment, getOptionEndAdornment);
    // ponytail: LAST_NAME_FIRST flips any option carrying first+last (person/employee) to "Last First";
    // no-ops for string-less function labels and options without both parts (e.g. companies, classifiers).
    const lastNameFirst = useLastNameFirst();
    const watchedValue = useWatch({ control, name });
    const isEmpty = watchedValue === undefined || watchedValue === null;

    const listRootWrapperRef = useRef<HTMLDivElement | null>(null);
    const [loaderEl, setLoaderEl] = useState<HTMLDivElement | null>(null);
    useInfiniteScroll(
        loaderEl,
        {
            observer: { root: listRootWrapperRef.current, rootMargin: '75px', threshold: 0 },
            disabled: isError,
            isLoading,
        },
        handleLoadMore
    );

    const getLabel = useCallback(
        (option: TOption | null) => {
            if (!option) {
                return null;
            }

            if (typeof getOptionLabel === 'string') {
                return optionDisplayLabel(option, lastNameFirst, get(option, getOptionLabel));
            } else {
                return getOptionLabel(option);
            }
        },
        [getOptionLabel, lastNameFirst]
    );

    const getValue = useCallback(
        (option: TOption | null) => {
            if (!option) {
                return null;
            }

            if (typeof getOptionIdentifier === 'string') {
                return get(option, getOptionIdentifier);
            } else {
                return getOptionIdentifier(option);
            }
        },
        [getOptionIdentifier]
    );

    const getDescription = useCallback(
        (option: TOption | null) => {
            if (!option) {
                return '';
            }
            if (!getOptionDescription) {
                return '';
            }

            if (typeof getOptionDescription === 'string') {
                return get(option, getOptionDescription);
            } else {
                return getOptionDescription(option);
            }
        },
        [getOptionDescription]
    );

    const getGroupedBy = (option: TOption | null) => {
        if (!option) {
            return fallbackTranslation;
        }

        if (groupByStaticFields) {
            return groupByStaticFields(option) ?? fallbackTranslation;
        }

        if (typeof groupByField === 'string') {
            return get(option, groupByField);
        }

        return fallbackTranslation;
    };

    // The list text the combobox puts in the input for the selected option, end adornment included
    // when that adornment is itself a string (e.g. ' (Inactive)').
    const itemToLabel = (option: TOption) => {
        if (typeof getOptionLabel !== 'string') {
            return getOptionLabel(option);
        }
        const baseLabel = optionDisplayLabel(option, lastNameFirst, get(option, getOptionLabel));
        const endAdornment = getOptionEndAdornment ? getEndAdornment(option) : null;
        return `${baseLabel}${typeof endAdornment === 'string' ? endAdornment : ''}`;
    };

    const isGrouped = !!(groupByStaticFields || groupByField);
    // The loaded page is already ordered by the group field, so the groups come out in the order
    // the rows arrive.
    const groupedOptions = useMemo<OptionGroup<TOption>[]>(() => {
        if (!isGrouped) {
            return [];
        }
        return groupConsecutiveOptions(dataToDisplay, (option) => String(getGroupedBy(option)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataToDisplay, isGrouped, groupByField, groupByStaticFields, fallbackTranslation]);

    const getSameNameEmail = useSameNameEmail(dataToDisplay);

    const renderOptionRow = (option: TOption) => {
        const optionKey = getValue(option);
        const sameNameEmail = getSameNameEmail(option);
        if (customOptionRenderer) {
            const { element, isDisabled } = customOptionRenderer(option, {}, getValue, sameNameEmail);
            return (
                <ComboboxItem key={optionKey} value={option} disabled={isDisabled}>
                    {element}
                </ComboboxItem>
            );
        }

        return (
            <ComboboxItem
                key={optionKey}
                value={option}
                // The row's accessible name stays its visible label, from its own content; `title` is the
                // supplementary channel — a native hover bubble, and the accessible description, which is
                // what the accname spec falls back to once a name comes from elsewhere. The description
                // wins; without one the bubble shows the whole label of a name the row cuts off.
                title={
                    (getOptionDescription ? getDescription(option) : undefined) ||
                    String(getLabel(option) ?? '') ||
                    undefined
                }
            >
                <div className="flex w-full min-w-0 items-center">
                    {getOptionStartAdornment ? (
                        <span className="mr-2 flex shrink-0 items-center">{getStartAdornment(option)}</span>
                    ) : null}
                    <span
                        className={cn(
                            'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap',
                            sameNameEmail && 'flex-1'
                        )}
                    >
                        <PersonOptionLabel option={option} label={getLabel(option)} sameNameEmail={sameNameEmail} />
                    </span>
                    {getOptionEndAdornment ? (
                        <span className="ml-2 flex shrink-0 items-center">{getEndAdornment(option)}</span>
                    ) : null}
                    {getOptionAction ? (
                        <span className="ml-auto flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
                            {getOptionAction(option)}
                        </span>
                    ) : null}
                </div>
            </ComboboxItem>
        );
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { ref, onChange: innerOnChange, onBlur, value }, fieldState: { error } }) => (
                <InputWrapper
                    name={name}
                    label={label}
                    shouldTranslateLabel={shouldTranslateLabel}
                    tooltip={tooltip}
                    required={required}
                    isEmpty={isEmpty}
                    readonly={!!readonly}
                    rightLabelComponent={rightLabelComponent}
                    bottomRightLabelComponent={bottomRightLabelComponent}
                    hasErrors={!!error}
                    error={readonly ? undefined : error}
                    className={wrapperClassName}
                    gap={gap}
                    noReadOnlyDivider={noReadOnlyDivider}
                >
                    {readonly ? (
                        <ReadonlyField
                            value={value}
                            getLabel={getLabel}
                            getValue={getValue}
                            linkTo={typeof readonly === 'object' ? readonly?.linkTo : undefined}
                        />
                    ) : (
                        <Combobox<TOption>
                            selectionAsPlaceholder
                            items={(isGrouped ? groupedOptions : dataToDisplay) as readonly TOption[]}
                            value={value ?? null}
                            disabled={disabled}
                            // Search runs on the server, so the popup shows exactly the page it was handed.
                            filter={null}
                            itemToStringLabel={itemToLabel}
                            isItemEqualToValue={(option, selectedOption) =>
                                getValue(option) === getValue(selectedOption)
                            }
                            open={isDropdownOpen}
                            onOpenChange={setOpen}
                            actionsRef={actionsRef}
                            onValueChange={(data) => {
                                innerOnChange(data);
                                if (onChange) {
                                    onChange(data);
                                }
                            }}
                            onInputValueChange={(data, details) => {
                                setInputValue(data);
                                // Only what the user typed drives a refetch; the input is also
                                // rewritten when an item is picked or the field loses focus, and
                                // searching for the value just chosen would throw the list away.
                                if (details.reason !== 'input-change' && details.reason !== 'input-clear') {
                                    if (details.reason === 'focus-out') {
                                        onSearchChange?.('blur', data);
                                    }
                                    return;
                                }
                                handleSearchChange(data);
                                onSearchChange?.(details.event?.type ?? details.reason, data);
                            }}
                        >
                            {(() => {
                                // Overlay the input with `{startAdornment}{label}{endAdornment}` so a node
                                // adornment reads as part of the name — a case's padlock leading it, a Persona
                                // non grata chip trailing it as PartyName shows elsewhere. Overlay is visible:
                                //   - dropdown closed: always (if value + adornment)
                                //   - dropdown open, input text still matches the selected label: visible
                                //   - dropdown open, user typing a search query: hidden so typed text shows
                                const optionEndAdornment =
                                    value && getOptionEndAdornment ? getEndAdornment(value) : null;
                                const selectedValueEndAdornment =
                                    value && getSelectedValueEndAdornment ? getSelectedValueEndAdornment(value) : null;
                                const selectedEndAdornment =
                                    optionEndAdornment != null || selectedValueEndAdornment != null ? (
                                        <>
                                            {optionEndAdornment}
                                            {selectedValueEndAdornment}
                                        </>
                                    ) : null;
                                const selectedStartAdornment =
                                    value && getSelectedValueStartAdornment
                                        ? getSelectedValueStartAdornment(value)
                                        : null;
                                const selectedLabel = getLabel(value);
                                const userIsSearching =
                                    isDropdownOpen && inputValue !== '' && inputValue !== selectedLabel;
                                const showSelectedOverlay =
                                    !!value &&
                                    (selectedStartAdornment != null || selectedEndAdornment != null) &&
                                    !userIsSearching;
                                return (
                                    <ComboboxInput
                                        id={name}
                                        name={name}
                                        ref={ref}
                                        className={cn(
                                            'bg-paper',
                                            showSelectedOverlay && '[&_input]:text-transparent',
                                            // A start adornment pushes the visible label right of the input's own
                                            // text, so the caret and selection would sit off the label.
                                            showSelectedOverlay &&
                                                selectedStartAdornment != null &&
                                                '[&_input]:caret-transparent [&_input]:selection:bg-transparent'
                                        )}
                                        title={(value && selectedLabel) || undefined}
                                        aria-invalid={!!error}
                                        aria-busy={isOpenPending || undefined}
                                        required={required ?? false}
                                        disabled={disabled}
                                        showClear={clearable !== false}
                                        placeholder={controlledPlaceholder}
                                        data-form-type="other"
                                        data-lpignore="true"
                                        onKeyDown={onInputKeyDown}
                                        onFocus={() => {
                                            if (placeholderOnFocus) {
                                                setControlledPlaceholder(t(placeholderOnFocus));
                                            }
                                        }}
                                        onBlur={() => {
                                            onBlur();
                                            dropPendingOpen();
                                            if (placeholderOnFocus) {
                                                setControlledPlaceholder(initialPlaceholder);
                                            }
                                        }}
                                    >
                                        {showSelectedOverlay && (
                                            <SelectedValueOverlay
                                                label={selectedLabel}
                                                startAdornment={selectedStartAdornment}
                                                endAdornment={selectedEndAdornment}
                                                clearable={clearable !== false}
                                            />
                                        )}
                                        {externalLoading || isOpenPending ? (
                                            <CircularLoader className="static mr-16 ml-auto group-has-[>[data-align=inline-end]>button]/input-group:mr-24 [&>span]:size-4" />
                                        ) : null}
                                    </ComboboxInput>
                                );
                            })()}
                            <ComboboxContent className="shadow-dropdown" positionerClassName={positionerClassName}>
                                <ComboboxEmpty>
                                    {isLoading ? (
                                        t('loading')
                                    ) : noOptionsContent ? (
                                        // The empty-state action ("Add new contact") opens a modal, and a press
                                        // inside the list does not dismiss it: closed here, or the list stays
                                        // open over that modal, its action still live.
                                        <span className="contents" onClick={() => setOpen(false)}>
                                            {noOptionsContent}
                                        </span>
                                    ) : (
                                        t('no-options')
                                    )}
                                </ComboboxEmpty>
                                {/* Mapped rather than handed to the list's function child: filtering is
                                    off here, and the load-more row has to sit inside the scroll box,
                                    which the function form leaves no room for. */}
                                <ComboboxList ref={listRootWrapperRef}>
                                    {isGrouped
                                        ? groupedOptions.map((group) => (
                                              <ComboboxGroup key={group.key} items={group.items}>
                                                  <ComboboxLabel className="flex items-center gap-1">
                                                      {getGroupStartAdornment
                                                          ? getGroupStartAdornment(group.value, group.items)
                                                          : null}
                                                      {group.value}
                                                  </ComboboxLabel>
                                                  <ComboboxCollection>
                                                      {(option: TOption) => renderOptionRow(option)}
                                                  </ComboboxCollection>
                                              </ComboboxGroup>
                                          ))
                                        : dataToDisplay.map((option) => renderOptionRow(option))}
                                    {hasNextPage ? (
                                        <div ref={setLoaderEl} className="relative py-2">
                                            <CircularLoader className="static [&>span]:size-6" />
                                        </div>
                                    ) : null}
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                    )}
                </InputWrapper>
            )}
        />
    );
};

export default DynamicSelectInput;
